/**
 * Phase 1: Centralized Query Service for Deduplication
 * 
 * Centralizes dashboard-related API calls with built-in deduplication,
 * caching, and auth-gating using React Query.
 * 
 * Configuration:
 * - staleTime: 30s (data considered fresh for 30 seconds)
 * - cacheTime: 5min (data kept in cache for 5 minutes)
 * - Auth-gated: Queries only run when authenticated
 */

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { selectCurrentUser, selectCurrentToken, selectCanManageUsersJWT } from '../features/auth/authSlice';
import dashboardService from './dashboardService';
import claimService from '../features/claims/claimService';
import userService from '../features/users/userService';

// Query Keys - Centralized for consistency
export const QUERY_KEYS = {
  DASHBOARD_STATS: ['dashboard', 'statistics'],
  RECENT_ACTIVITY: ['dashboard', 'recent-activity'],
  CLAIMS: ['claims'],
  USERS: ['users'],
};

// Default Query Configuration for Dashboard Endpoints
const DASHBOARD_QUERY_CONFIG = {
  staleTime: 30 * 1000, // 30 seconds - data considered fresh
  gcTime: 5 * 60 * 1000, // 5 minutes - cache retention time
  retry: 2,
  refetchOnWindowFocus: false,
  refetchOnMount: 'always', // Always refetch on mount for fresh data
};

/**
 * Hook for fetching dashboard statistics with deduplication
 * Replaces useDashboardStats hook
 */
export const useDashboardStatsQuery = () => {
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = !!(user && token);

  return useQuery({
    queryKey: QUERY_KEYS.DASHBOARD_STATS,
    queryFn: () => dashboardService.getStatistics(),
    enabled: isAuthenticated, // Only run when authenticated
    ...DASHBOARD_QUERY_CONFIG,
  });
};

/**
 * Hook for fetching recent activity with deduplication  
 * Replaces useRecentActivity hook
 */
export const useRecentActivityQuery = () => {
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = !!(user && token);

  return useQuery({
    queryKey: QUERY_KEYS.RECENT_ACTIVITY,
    queryFn: () => dashboardService.getRecentActivity(),
    enabled: isAuthenticated, // Only run when authenticated
    ...DASHBOARD_QUERY_CONFIG,
    select: (data) => {
      // The backend might return data in different formats
      // Let's handle common patterns
      
      // Handle different response formats
      if (Array.isArray(data)) {
        return data; // Already an array
      } else if (data?.activities) {
        return data.activities; // Wrapped in activities property
      } else if (data?.data) {
        return data.data; // Wrapped in data property
      } else if (data?.recentActivity) {
        return data.recentActivity; // Wrapped in recentActivity property
      }
      
      // Fallback - return empty array
      console.warn('🔄 [queryService] Unknown recent activity response format, returning empty array');
      return [];
    }
  });
};

/**
 * Hook for fetching claims data with deduplication
 * For dashboard display - minimal data needed
 * Returns the claims response object with pagination
 */
export const useClaimsQuery = () => {
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const isAuthenticated = !!(user && token);

  return useQuery({
    queryKey: QUERY_KEYS.CLAIMS,
    queryFn: () => claimService.getAllClaims(),
    enabled: isAuthenticated, // Only run when authenticated
    ...DASHBOARD_QUERY_CONFIG,
    select: (data) => {
      // The claimService.getAllClaims returns { claims: [...], pagination: {...} }
      // We want to return this structure to the hook consumer
      return data;
    }
  });
};

/**
 * Hook for fetching users data with deduplication
 * Only runs when user has 'users:read' permission (Admin role)
 */
export const useUsersQuery = () => {
  const user = useSelector(selectCurrentUser);
  const token = useSelector(selectCurrentToken);
  const canManageUsers = useSelector(selectCanManageUsersJWT);
  const isAuthenticated = !!(user && token);

  return useQuery({
    queryKey: QUERY_KEYS.USERS,
    queryFn: () => userService.getAllUsers(),
    enabled: isAuthenticated && canManageUsers, // Only run if authenticated AND has permission
    ...DASHBOARD_QUERY_CONFIG,
  });
};
